/**
 * The homepage trip planners (Quote First's "Plan your trip" card, the Tours search
 * bar): a passenger stepper that suggests the right vehicle, and service tabs. The
 * form itself is a plain GET to /quote, which opens the full quote form pre-filled.
 *
 * Quote First's tabs are radio buttons named service_type, one per form type, each
 * carrying data-mode; picking one shows that type's fields (form-modes.js). The date
 * the visitor already chose moves to the date field of the new tab.
 */
import { attachPlaces } from './places';
import { applyFormMode } from './form-modes';

function suggestion(types, pax) {
    const fits = types.filter((t) => pax >= t.min && pax <= t.max).sort((a, b) => a.max - b.max);
    if (fits.length) {
        return fits[0];
    }
    const largest = [...types].sort((a, b) => b.max - a.max)[0];
    if (largest && pax > largest.max) {
        return { ...largest, many: Math.ceil(pax / largest.max) };
    }
    return types.sort((a, b) => a.min - b.min)[0] || null;
}

function initPlanner(form) {
    let types = [];
    try {
        types = JSON.parse(form.dataset.vehicleTypes || '[]');
    } catch (e) {
        types = [];
    }
    const paxInput = form.querySelector('[data-pax-input]');
    const paxLabel = form.querySelector('[data-pax-label]');
    const vehicleInput = form.querySelector('[name="vehicle_type"]');
    const matchName = form.querySelector('[data-match-name]');
    const matchSeats = form.querySelector('[data-match-seats]');
    const strings = { vehicles: form.dataset.labelVehicles || 'vehicles', seats: form.dataset.labelSeats || 'seats' };

    const render = () => {
        if (!paxInput) {
            return;
        }
        const pax = Math.max(1, Math.min(300, parseInt(paxInput.value, 10) || 1));
        paxInput.value = String(pax);
        if (paxLabel) {
            paxLabel.textContent = String(pax);
        }
        const match = suggestion(types, pax);
        if (match && matchName) {
            matchName.textContent = match.many ? `${match.many} × ${match.name}` : match.name;
            if (matchSeats) {
                matchSeats.textContent = `${match.min}–${match.max} ${strings.seats}`;
            }
            if (vehicleInput) {
                vehicleInput.value = match.many ? '' : String(match.id);
            }
        }
    };

    form.querySelectorAll('[data-pax-step]').forEach((button) => {
        button.addEventListener('click', () => {
            const current = parseInt(paxInput.value, 10) || 1;
            const up = button.dataset.paxStep === 'up';
            const step = up ? (current >= 20 ? 5 : 1) : (current > 20 ? 5 : 1);
            paxInput.value = String(up ? current + step : Math.max(1, current - step));
            render();
        });
    });
    paxInput?.addEventListener('input', render);

    // Service tabs are radio buttons named service_type (the arrow keys move between them).
    const dateInputs = [...form.querySelectorAll('input[name="service_date"]')];
    const switchTo = (mode) => {
        const before = dateInputs.find((input) => !input.disabled)?._flatpickr?.selectedDates[0];
        applyFormMode(form, mode);
        const after = dateInputs.find((input) => !input.disabled);
        if (before && after?._flatpickr && !after._flatpickr.selectedDates.length) {
            after._flatpickr.setDate(before, false);
        }
    };
    form.querySelectorAll('[data-tab-radio]').forEach((radio) => {
        radio.addEventListener('change', () => {
            if (radio.dataset.mode) {
                switchTo(radio.dataset.mode);
            }
            form.dispatchEvent(new CustomEvent('planner:service', { detail: radio.value }));
        });
    });
    const checked = form.querySelector('[data-tab-radio][data-mode]:checked');
    if (checked) {
        applyFormMode(form, checked.dataset.mode);
    }

    render();
}

export function initTripPlanner() {
    document.querySelectorAll('form[data-trip-planner]').forEach(initPlanner);
    attachPlaces();
}
